import { StaticDataItem } from "../../models/types/statistic";
import { useLookupParameter } from "../../utils/get-lookup";
import dayjs from "dayjs";
import { Line } from 'react-chartjs-2';
import { useState, useEffect } from "react";
import Tooltip from '@mui/material/Tooltip';
import { useResponsive } from "../../hooks/mediaQuery";

const StatisticChart = ({ staticData }: { staticData: StaticDataItem[] }) => {
    const { lookupParameter } = useLookupParameter();
    const { isXs } = useResponsive();

    const columns = Array.from(
        new Set(
            staticData?.flatMap((item: any) =>
                Array.isArray(item.data) ? item.data.map((d: any) => {
                    return d.parameterId;
                }) : []
            )
        )
    );
    const allParams = columns;
    const [visibleParams, setVisibleParams] = useState<string[]>([]);

    const chartData = (() => {
        if (!staticData || !Array.isArray(staticData) || staticData.length === 0) return null;
        const allDates = Array.from(new Set(staticData.map(item => item.waterMonitoringTime))).sort();

        let datasets: any[] = [];
        allParams.filter(paramId => visibleParams.includes(paramId)).map((paramId, idx) => {
            const _data = lookupParameter.find((p: any) => p.id === paramId)
            const colors = [
                '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099',
                '#3B3EAC', '#0099C6', '#DD4477', '#66AA00', '#B82E2E',
                '#316395', '#994499', '#22AA99', '#AAAA11', '#6633CC',
                '#E67300', '#8B0707', '#329262', '#5574A6', '#3B3EAC'
            ];
            const unit = _data?.unitName;
            const column = _data?.id;
            datasets = [...datasets, {
                label: `${_data?.name} (${unit})`,
                data: allDates.map(date => {
                    const found = staticData.find(item => item.waterMonitoringTime === date);
                    return found ? found.data?.find((d: any) => d.parameterId === column)?.value : null;
                }),
                borderColor: colors[idx % colors.length],
                backgroundColor: colors[idx % colors.length],
                spanGaps: true,
                fill: false,
                tension: 0.4,
            },
            {
                label: 'Giới hạn tối thiểu',
                data: (() => {
                    const firstStandardMin = allDates
                        .map(date => {
                            const found = staticData.find(item => item.waterMonitoringTime === date);
                            return found ? found.data?.find((d: any) => d.parameterId === column)?.standardMin : null;
                        })
                        .find(val => val !== null && val !== undefined);
                    return allDates.map(() => firstStandardMin ?? null);
                })(),
                borderColor: 'black',
                borderWidth: 2,
                borderDash: [8, 4], // Nét đứt
                pointRadius: 0,
                fill: false,
                tension: 0,
            },
            {
                label: 'Giới hạn tối đa',
                data: (() => {
                    const firstStandardMax = allDates
                        .map(date => {
                            const found = staticData.find(item => item.waterMonitoringTime === date);
                            return found ? found.data?.find((d: any) => d.parameterId === column)?.standardMax : null;
                        })
                        .find(val => val !== null && val !== undefined);
                    return allDates.map(() => firstStandardMax ?? null);
                })(),
                borderColor: 'red',
                borderWidth: 2,
                borderDash: [8, 4], // Nét đứt
                pointRadius: 0,
                fill: false,
                tension: 0,
            }
            ];
        });
        return {
            labels: allDates.map(date => dayjs(date).format('DD-MM-YYYY HH:mm:ss')),
            datasets,
        };
    })()

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            // title: {
            //     display: true,
            //     text: 'Biểu đồ các chỉ số nước',
            // },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
            },
        },
        interaction: {
            mode: 'nearest' as const,
            axis: 'x' as const,
            intersect: false,
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    useEffect(() => {
        if (visibleParams.length === 0 && allParams.length > 0) {
            setVisibleParams([allParams[0]]);
        }
    }, [visibleParams, allParams, setVisibleParams]);


    const handleToggleParam = (paramId: string) => {
        // setVisibleParams(prev =>
        //     prev.includes(paramId)
        //         ? prev.filter(k => k !== paramId)
        //         : [...prev, paramId]
        // );
        setVisibleParams([paramId]);
    };
    return (
        <div className="w-full py-4">
            {chartData && (
                <div className={`w-full ${isXs ? 'h-[300px]' : 'h-[500px]'}`}>
                    <Line data={chartData} options={chartOptions} />
                </div>
            )}
            <div className="flex justify-center flex-wrap gap-3 my-2">
                {allParams.length === 0 ? (
                    <span className="text-gray-400 text-xs">Chưa có dữ liệu chỉ số</span>
                ) : (
                    allParams.map(paramId => {
                        const parameter = lookupParameter.find((p: any) => p.id === paramId);
                        const textHover = `${parameter?.name} (${parameter?.unitName || "-"})`;
                        return (
                            <Tooltip title={textHover} arrow placement="top">
                                <button
                                    key={paramId}
                                    className={`px-2 py-1 rounded text-xs border ${visibleParams.includes(paramId) ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                    onClick={() => handleToggleParam(paramId)}
                                >
                                    {parameter?.name} ({parameter?.unitName || "-"})
                                </button>
                            </Tooltip>
                        )
                    })
                )}
            </div>
        </div>
    )
}

export default StatisticChart;
